/**
 * Atlas PM · 系统设置 · 项目类型标签页（P4.5）
 *
 * 列表列：代码 / 名称 / 颜色 / 排序 / 状态 / 操作。
 * 操作：新建（弹窗）、编辑（弹窗，code 置灰）、删除（二次确认 + DICT_IN_USE 原样提示）。
 *
 * 权限：dict:manage；数据源：GET /api/system/project-types。
 */

import { useEffect, useState } from 'react';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import Panel from '../../components/Panel';
import Spinner from '../../components/Spinner';
import type { ProjectTypeItem } from './project-types/api';
import { useProjectTypes } from './project-types/useProjectTypes';
import ProjectTypeFormDialog from './project-types/ProjectTypeFormDialog';
import DeleteProjectTypeDialog from './project-types/DeleteProjectTypeDialog';
import './project-types/project-types.css';

/** 颜色单元格：色块 + 色值（无颜色时占位） */
function ColorCell({ color }: { color: string | null }) {
  return (
    <span className="pt-color-cell">
      {color ? (
        <i className="pt-swatch pt-swatch--sm" style={{ background: color }} />
      ) : (
        <i className="pt-swatch pt-swatch--sm pt-swatch--none" />
      )}
      <span className="num pt-color-text">{color ?? '—'}</span>
    </span>
  );
}

export default function ProjectTypesPanel() {
  const query = useProjectTypes();

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<ProjectTypeItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<ProjectTypeItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const items = query.data ?? [];

  const openCreate = () => {
    setEditItem(null);
    setFormOpen(true);
  };

  const openEdit = (it: ProjectTypeItem) => {
    setEditItem(it);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditItem(null);
  };

  const handleSaved = (saved: ProjectTypeItem, mode: 'create' | 'edit') => {
    closeForm();
    setToast(
      mode === 'create'
        ? `已新建项目类型「${saved.name}」`
        : `已保存项目类型「${saved.name}」`,
    );
  };

  const handleDeleted = (id: string) => {
    const name = deleteItem?.id === id ? deleteItem.name : '';
    setDeleteItem(null);
    setToast(name ? `已删除项目类型「${name}」` : '已删除项目类型');
  };

  const errorMessage =
    query.error instanceof Error ? query.error.message : '项目类型加载失败';

  return (
    <Panel
      title="项目类型"
      extra={
        <div className="pt-head">
          <Badge tone="brand">{items.length} 个类型</Badge>
          <Button variant="primary" size="sm" data-testid="pt-add" onClick={openCreate}>
            ＋ 新建类型
          </Button>
        </div>
      }
      flush
    >
      {toast && (
        <div className="pt-toast" role="status" data-testid="pt-toast">
          <span aria-hidden="true">✔</span>
          <span>{toast}</span>
        </div>
      )}

      {query.isLoading ? (
        <Spinner label="加载项目类型…" />
      ) : query.isError ? (
        <div className="pt-error" data-testid="pt-error">
          <div>⚠ {errorMessage}</div>
          <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
            重试
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="▣" title="暂无项目类型" hint="点击右上角「新建类型」创建" />
      ) : (
        <div className="pt-table-wrap">
          <table className="pt-table" data-testid="pt-table">
            <thead>
              <tr>
                <th>代码</th>
                <th>名称</th>
                <th>颜色</th>
                <th>排序</th>
                <th>状态</th>
                <th className="pt-th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid="pt-row" data-code={it.code}>
                  <td>
                    <span className="pt-code num">{it.code}</span>
                  </td>
                  <td className="pt-name">{it.name}</td>
                  <td>
                    <ColorCell color={it.color} />
                  </td>
                  <td className="num">{it.sortOrder}</td>
                  <td>
                    <Badge tone={it.isActive ? 'ok' : 'idle'}>
                      {it.isActive ? '启用' : '停用'}
                    </Badge>
                  </td>
                  <td>
                    <div className="pt-actions">
                      <Button
                        size="sm"
                        variant="secondary"
                        data-testid="pt-edit"
                        data-code={it.code}
                        onClick={() => openEdit(it)}
                      >
                        编辑
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        data-testid="pt-delete"
                        data-code={it.code}
                        onClick={() => setDeleteItem(it)}
                      >
                        删除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProjectTypeFormDialog
        open={formOpen}
        mode={editItem ? 'edit' : 'create'}
        item={editItem}
        onClose={closeForm}
        onSaved={handleSaved}
      />

      <DeleteProjectTypeDialog
        open={Boolean(deleteItem)}
        item={deleteItem}
        onClose={() => setDeleteItem(null)}
        onDeleted={handleDeleted}
      />
    </Panel>
  );
}
